import type { ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import type { RouteObject } from 'react-router';
import { RouterProvider, createBrowserRouter } from 'react-router';

import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import { AppConfigContextProvider } from '#/config/AppConfigContext';
import { UserEnvironmentContextProvider } from '#/contexts/UserEnvironmentContext';
import { I18nContext } from '#/i18n/contexts/I18nContext';
import { createI18nFn } from '#/i18n/createI18nFn';
import { RenderingClientInnerWrapper } from '#/rendering/client/components/RenderingClientInnerWrapper';
import { setClientHydrated } from '#/rendering/state/renderingState';
import { routeObjectsWithRootErrorBoundary } from '#/routing/utils/routeObjectsWithRootErrorBoundary';

import { configureUserTracking } from './utils/configureUserTracking';
import { renderingBootstrapClient } from './utils/renderingBootstrapClient';
import { renderingWaitForInitUserEnvironmentToBeSet } from './utils/renderingWaitForInitUserEnvironmentToBeSet';

type Args = {
  Component: ComponentType<Record<never, never>>;
  routeObjects: RouteObject[];
};

/**
 * Client-only rendering (no Server Side Rendering)
 *
 * This type of rendering should only be used for private pages because
 * they can't be indexed properly by crawlers
 */
export async function renderClientNoSsr({ Component, routeObjects }: Args) {
  setClientHydrated();

  // Set config and as soon as possible
  const { config } = window.Idealist;
  const { locale } = config.idealist;

  // Bootstrap client
  renderingBootstrapClient(config);

  // Render initial spinner
  const root = createRoot(document.getElementById('root')!);
  root.render(<LoadingSection />);

  // Load user environment
  const userEnvironment = await renderingWaitForInitUserEnvironmentToBeSet();
  configureUserTracking({
    user: userEnvironment.user,
    environment: userEnvironment.environment,
    locale: locale,
  });

  // Setup react-router & Idealist wrappers
  const wrappedRoutes = [
    {
      element: (
        <AppConfigContextProvider appConfig={config}>
          <I18nContext value={{ locale: locale, i18n: createI18nFn(locale) }}>
            <RenderingClientInnerWrapper>
              <UserEnvironmentContextProvider
                initialState={{
                  user: userEnvironment.user,
                  environment: userEnvironment.environment,
                  actionsWhileLoggedOutProcessedAt: null,
                }}
              >
                <Component />
              </UserEnvironmentContextProvider>
            </RenderingClientInnerWrapper>
          </I18nContext>
        </AppConfigContextProvider>
      ),
      children: routeObjectsWithRootErrorBoundary(routeObjects),
    },
  ];

  const router = createBrowserRouter(wrappedRoutes);

  // Render
  root.render(<RouterProvider router={router} />);
}
